import React from 'react';
import { canonicalFor, usePageMeta } from '../../hooks/usePageMeta';
import { PageHeader } from '../../components/page/PageHeader';
import { ContentSection } from '../../components/page/ContentSection';
import { SpecList } from '../../components/page/SpecList';
import { CautionNote } from '../../components/page/CautionNote';
import { TechAssistCallout } from '../../components/page/TechAssistCallout';
import { RelatedLinks } from '../../components/page/RelatedLinks';
export function CopperSoftAlloys() {
  usePageMeta({
    title: 'Copper & Soft Alloy Peck-Cycle Guidance | Robertson Precision',
    description: 'Published Shear Geometry peck-cycle guidance for copper and soft aluminum alloys, with oxygen-free copper starting values: 1,000 SFM, .050 in DOC, .025 IPR.',
    canonical: canonicalFor('/applications/copper-soft-alloys/')
  });
  return <>
      <PageHeader eyebrow="Applications" title="Copper & Soft Aluminum Alloys" lead="The published guidance calls for a peck cycle for chip control in copper and soft aluminum alloys. Specific starting values are published for oxygen-free copper only." crumbs={[{
      label: 'Home',
      href: '/'
    }, {
      label: 'Applications',
      href: '/applications/'
    }, {
      label: 'Copper & Soft Alloys'
    }]} data-id="element-714" data-mp="2nt8xx1vis0io" />

      <ContentSection id="ofc" title="Oxygen-free copper — starting point" intro={<p data-id="element-716" data-mp="1cim9x6i4tw1m">
            The copper values are published without a specific insert part
            number. Talk to the technical staff about insert selection.
          </p>} data-id="element-715" data-mp="208gl7jmszcjr">
        <SpecList ariaLabel="Oxygen-free copper starting guidance" specs={[{
        label: 'Speed',
        value: '1,000 SFM'
      }, {
        label: 'DOC per side',
        value: 'Start .050 in'
      }, {
        label: 'Feed',
        value: 'Start .025 IPR'
      }, {
        label: 'Peck',
        value: '.015 in feed / .010 in retract'
      }]} data-id="element-717" data-mp="1lklprvheuu2z" />
      </ContentSection>

      <ContentSection id="peck" tone="warm" title="Peck movement vs. feed" data-id="element-718" data-mp="1pkwx4quvyrux">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-16" data-id="element-719" data-mp="1uk9s5yvwv58l">
          <div className="rp-measure space-y-4 text-[15.5px] leading-[1.7] text-graphite/85" data-id="element-720" data-mp="1umma4c1o5fln">
            <p data-id="element-721" data-mp="1zhrxzgemt4cd">
              The <strong className="font-semibold text-navy" data-id="element-722" data-mp="23l7pjybugei7">feed</strong>{' '}
              (.025 IPR) is how far the tool advances for each spindle
              revolution.
            </p>
            <p data-id="element-723" data-mp="1bubbwvhym5tv">
              The <strong className="font-semibold text-navy" data-id="element-724" data-mp="1bts4i8pxqzu3">peck</strong>{' '}
              values are incremental moves within the cycle: advance .015 in,
              then retract .010 in, to break the chip. They are separate
              settings from the feed rate.
            </p>
          </div>
          <CautionNote title="Other alloys and machines" data-id="element-725" data-mp="o58wluassp2j">
            <p data-id="element-726" data-mp="o2mre1bdqlnh">
              These are starting values for oxygen-free copper and don’t
              transfer to other alloys. Lower-power machines may require a peck
              cycle for other materials too.
            </p>
            <p data-id="element-727" data-mp="fnyjr43pb8an">
              The catalog also mentions brass and plastics but gives no
              grade-specific settings for them. Confirm your setup with the
              technical staff.
            </p>
          </CautionNote>
        </div>
      </ContentSection>

      <ContentSection id="help" data-id="element-728" data-mp="6p4opi60qpz7">
        <TechAssistCallout location="copper" data-id="element-729" data-mp="1oh0t5d36p97" />
      </ContentSection>

      <RelatedLinks links={[{
      label: 'Starting Data',
      href: '/technical-resources/starting-data/',
      description: 'All materials in one table.'
    }, {
      label: 'Workholding & Safety',
      href: '/technical-resources/safety/',
      description: 'Setup checks before cutting.'
    }, {
      label: 'Inserts & Chipbreakers',
      href: '/products/inserts/',
      description: 'The seven APGX inserts.'
    }, {
      label: 'Aluminum',
      href: '/applications/aluminum/',
      description: '6061-T6 roughing and chip control.'
    }]} data-id="element-730" data-mp="1ps4l56j7x0b" />
    </>;
}